import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { styles } from '../styles';
import { github, modalBg } from '../assets';
import { SectionWrapper } from '../hoc';
import { projects } from '../constants';
import { fadeIn, textVariant } from '../utils/motion';

const CaseStudySection = ({ title, content, icon }) => (
	<div className="mt-5">
		<h4 className="text-primary text-[14px] font-bold flex items-center gap-2 uppercase tracking-wide">
			<span className="w-6 h-6 rounded-md bg-accent/10 text-accent text-[11px] font-bold flex items-center justify-center flex-shrink-0">
				{icon}
			</span>
			{title}
		</h4>
		<p className="mt-1.5 text-muted text-[14px] leading-relaxed">{content}</p>
	</div>
);

const ProjectModal = ({ project, onClose }) => {
	if (!project) return null;

	const hasCaseStudy = project.problem && project.architecture;

	const modalContent = (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.2 }}
			className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
			onClick={onClose}>
			<motion.div
				initial={{ opacity: 0, scale: 0.95, y: 20 }}
				animate={{ opacity: 1, scale: 1, y: 0 }}
				exit={{ opacity: 0, scale: 0.95, y: 20 }}
				transition={{ duration: 0.3 }}
				className="bg-warm rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
				onClick={(e) => e.stopPropagation()}>

				<div className="relative flex-shrink-0">
					<div
						className="relative flex items-center justify-center rounded-t-2xl overflow-hidden h-[260px]"
						style={{
							backgroundImage: `url(${modalBg})`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
						}}>
						<img
							src={project.image}
							alt={project.name}
							className="relative z-10 max-w-[90%] max-h-[230px] object-contain drop-shadow-2xl rounded-lg"
						/>
					</div>
					<button
						onClick={onClose}
						className="absolute top-3 right-3 w-8 h-8 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
						aria-label="Close modal">
						<svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				<div className="flex-1 overflow-y-auto p-6 sm:p-8">
					<h3 className="text-primary text-[22px] font-bold">{project.name}</h3>

					{hasCaseStudy ? (
						<>
							<CaseStudySection title="Problem" content={project.problem} icon="01" />
							<CaseStudySection title="Constraints" content={project.constraints} icon="02" />
							<CaseStudySection title="Architecture" content={project.architecture} icon="03" />
							<CaseStudySection title="Tradeoffs" content={project.tradeoffs} icon="04" />
							<CaseStudySection title="Outcome" content={project.outcome} icon="05" />
						</>
					) : (
						<p className="mt-4 text-muted text-[15px] leading-relaxed">{project.description}</p>
					)}

					<div className="mt-6 flex flex-wrap gap-2">
						{project.tags.map((tag) => (
							<span
								key={tag.name}
								className="px-3 py-1 bg-surface border border-border rounded-md text-[13px] text-muted font-medium">
								{tag.name}
							</span>
						))}
					</div>

					<div className="mt-6">
						{project.visibility === 'private' ? (
							<div className="bg-surface border border-border rounded-lg p-4">
								<p className="text-primary text-[14px] font-semibold flex items-center gap-2">
									<svg className="w-4 h-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 .552-.448 1-1 1s-1-.448-1-1 .448-1 1-1 1 .448 1 1zm5-4v2m-2-2V7a4 4 0 00-8 0v2m12 0H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2z" />
									</svg>
									Private Implementation
								</p>
								<p className="mt-2 text-muted text-[13px] leading-relaxed">
									{project.implementation_note}
								</p>
							</div>
						) : (
							<a
								href={project.source_code_link}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-2 bg-accent text-white px-5 py-2.5 rounded-lg text-[14px] font-medium hover:bg-accent-dark transition-colors">
								<img src={github} alt="" className="w-4 h-4 invert" aria-hidden="true" />
								View Source
							</a>
						)}
					</div>
				</div>
			</motion.div>
		</motion.div>
	);

	return createPortal(modalContent, document.body);
};

const ProjectCard = ({ index, project, onClick }) => {
	return (
		<motion.div
			variants={fadeIn('up', 'tween', index * 0.1, 0.4)}
			className="group bg-warm border border-border rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
			onClick={() => onClick(project)}>
			<div className="relative h-40 overflow-hidden">
				<img
					src={project.image}
					alt={`Screenshot of ${project.name}`}
					className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
				/>
			</div>

			<div className="p-5">
				<div className="flex items-start justify-between gap-3">
					<h3 className="text-primary font-bold text-[18px]">{project.name}</h3>
					{project.visibility === 'private' && (
						<span className="px-2 py-0.5 bg-surface border border-border rounded text-[11px] text-muted font-medium flex-shrink-0">
							Private
						</span>
					)}
				</div>
				<p className="mt-2 text-muted text-[14px] leading-relaxed line-clamp-2">
					{project.description}
				</p>

				<div className="mt-4 flex flex-wrap gap-1.5">
					{project.tags.slice(0, 3).map((tag) => (
						<span
							key={tag.name}
							className="px-2 py-0.5 bg-surface text-[12px] text-muted font-medium rounded">
							{tag.name}
						</span>
					))}
					{project.tags.length > 3 && (
						<span className="px-2 py-0.5 text-[12px] text-muted">
							+{project.tags.length - 3}
						</span>
					)}
				</div>

				<div className="mt-4 flex items-center text-accent text-[14px] font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
					View Case Study
					<svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
					</svg>
				</div>
			</div>
		</motion.div>
	);
};

const Works = () => {
	const [selectedProject, setSelectedProject] = useState(null);

	useEffect(() => {
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = selectedProject ? 'hidden' : '';

		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [selectedProject]);

	return (
		<>
			<motion.div variants={textVariant()}>
				<p className={styles.sectionSubText}>Engineering Decisions</p>
				<h2 className={styles.sectionHeadText}>Case Studies</h2>
			</motion.div>

			<motion.p
				variants={fadeIn('', '', 0.1, 0.4)}
				className="mt-4 text-muted text-[16px] w-full leading-[28px]">
				Each project below is a real engineering problem I solved. Click to see the
				problem, constraints, architecture decisions, tradeoffs I made, and outcomes.
			</motion.p>

			<div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
				{projects.map((project, index) => (
					<ProjectCard
						key={`project-${index}`}
						index={index}
						project={project}
						onClick={setSelectedProject}
					/>
				))}
			</div>

			<AnimatePresence>
				{selectedProject && (
					<ProjectModal
						project={selectedProject}
						onClose={() => setSelectedProject(null)}
					/>
				)}
			</AnimatePresence>
		</>
	);
};

export default SectionWrapper(Works, 'projects');
